import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, X, Pencil, Images } from "lucide-react";
import { downscaleImage } from "@/lib/checklist";
import { isLazyPhoto, resolveLazyPhoto } from "@/lib/lazyPhotos";
import LazyImg from "@/components/LazyImg";
import PhotoAnnotator from "@/components/PhotoAnnotator";
import CameraBurst from "@/components/CameraBurst";
import VideoStrip from "@/components/VideoStrip";
import { backupPhotosToDevice } from "@/lib/photoBackup";
import { toast } from "sonner";

const PhotoStrip = ({ photos = [], onChange, testidPrefix, maxPhotos = 20, backupLabel, inspectionId, itemId }) => {
  const ref = useRef(null);
  const { id: routeId } = useParams();
  const inspId = inspectionId || routeId;
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingSrc, setEditingSrc] = useState(null);
  const [burstOpen, setBurstOpen] = useState(false);
  const [videoRefresh, setVideoRefresh] = useState(0);
  const remaining = Math.max(0, maxPhotos - (photos?.length || 0));

  const openAnnotator = async (idx) => {
    let src = photos[idx];
    if (isLazyPhoto(src)) {
      try { src = await resolveLazyPhoto(inspId, src); }
      catch { toast.error("Photo not available offline yet"); return; }
    }
    setEditingSrc(src);
    setEditingIdx(idx);
  };

  const onAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const datas = await Promise.all(files.map((f) => downscaleImage(f)));
      onChange([...(photos || []), ...datas]);
    } catch {
      toast.error("Could not read photo(s)");
    }
    e.target.value = "";
  };

  const remove = (idx) => onChange(photos.filter((_, i) => i !== idx));

  const replaceAt = (idx, dataUrl) =>
    onChange(photos.map((p, i) => (i === idx ? dataUrl : p)));

  return (
    <div>
      <input
        ref={ref} type="file" accept="image/*" multiple className="hidden"
        onChange={onAdd} data-testid={`${testidPrefix}-photo-input`}
      />
      <div className="flex flex-wrap gap-2 mt-2">
        {(photos || []).map((url, idx) => (
          <div key={idx} className="relative group border-2 border-slate-200 w-32 h-32">
            <LazyImg src={url} inspectionId={inspId} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => openAnnotator(idx)}
              data-testid={`${testidPrefix}-photo-annotate-${idx}`}
              className="absolute inset-0 bg-slate-900/0 hover:bg-slate-900/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
              aria-label="Annotate photo"
            >
              <span className="inline-flex items-center gap-1 text-white text-xs font-bold uppercase tracking-wider bg-slate-900/80 px-2 py-1">
                <Pencil size={12} strokeWidth={2.5} /> Annotate
              </span>
            </button>
            <button
              type="button" onClick={() => remove(idx)}
              data-testid={`${testidPrefix}-photo-remove-${idx}`}
              className="absolute -top-2 -right-2 bg-white border-2 border-slate-900 w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
              aria-label="Remove photo"
            >
              <X size={12} strokeWidth={3} />
            </button>
          </div>
        ))}
        {remaining > 0 && (
          <>
            {/* Camera burst — take multiple shots in one session */}
            <button
              type="button" onClick={() => setBurstOpen(true)}
              data-testid={`${testidPrefix}-photo-camera`}
              className="w-32 h-32 bg-[#1E1B4B] hover:bg-[#0F0E2E] text-white flex flex-col items-center justify-center text-xs font-bold"
            >
              <Camera size={20} strokeWidth={2.5}/>
              <span className="mt-1">Camera</span>
            </button>
            {/* Gallery multi-picker */}
            <button
              type="button" onClick={() => ref.current?.click()}
              data-testid={`${testidPrefix}-photo-add`}
              className="w-32 h-32 border-2 border-dashed border-slate-300 hover:border-slate-900 hover:bg-slate-50 flex flex-col items-center justify-center text-slate-500 text-xs font-bold"
            >
              <Images size={20} strokeWidth={2.5} />
              <span className="mt-1">Gallery</span>
            </button>
          </>
        )}
      </div>

      {inspectionId && (
        <VideoStrip inspectionId={inspectionId} itemId={itemId} refreshKey={videoRefresh} testidPrefix={testidPrefix} />
      )}

      <PhotoAnnotator
        open={editingIdx !== null}
        src={editingIdx !== null ? editingSrc : null}
        onClose={() => { setEditingIdx(null); setEditingSrc(null); }}
        onSave={(data) => replaceAt(editingIdx, data)}
      />

      <CameraBurst
        open={burstOpen}
        remaining={remaining}
        inspectionId={inspectionId}
        itemId={itemId}
        onVideoSaved={() => setVideoRefresh((k) => k + 1)}
        onClose={() => setBurstOpen(false)}
        onDone={(dataUrls) => {
          setBurstOpen(false);
          if (dataUrls?.length) {
            backupPhotosToDevice(dataUrls, backupLabel || "checklist");
            onChange([...(photos || []), ...dataUrls]);
          }
        }}
      />
    </div>
  );
};

export default PhotoStrip;
